import axios from "axios";
import firebase from "./firebase";

const parseFilenameFromContentDispositionHeader = (cd) => {
  if (!cd) return null;
  // RFC 5987: filename*=UTF-8''xxx
  const star = /filename\*\s*=\s*[^']+''([^;]+)/i.exec(cd);
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1]);
    } catch (_e) {
      return null;
    }
  }
  const m = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
  return m ? m[1] : null;
};

export const fetchGPReport = async (id) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_GP_REPORT}/${id}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
    responseType: "blob",
  });
};

export const fetchGPReportFromPMS = async (pmsFileId) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_PMS_GP_REPORT}/${pmsFileId}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
    responseType: "blob",
  });
};

export const downloadGPReport = (blob, filename) => {
  // Preserve the MIME type axios put on the source Blob (responseType
  // "blob"). Dropping it forces the browser to guess from the filename, and
  // for legacy GCS records whose `name` lacks an extension that guess used
  // to land on `text/plain` — saving PDF bytes as `<patient>.txt`.
  const parts = Array.isArray(blob) ? blob : [blob];
  const sourceType = parts[0] && parts[0].type;
  const url = window.URL.createObjectURL(sourceType ? new Blob(parts, { type: sourceType }) : new Blob(parts));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadPMSGPReport = (blob, pmsFileId, headers) => {
  // Axios + responseType "blob" gives res.data as Blob; wrap in a single-length array for Blob().
  // Do not pass [res.data] from callers — that would become new Blob([[blob]]) and stringify to garbage.
  const parts = Array.isArray(blob) ? blob : [blob];
  const contentType = headers["content-type"] || "application/octet-stream";
  const filename =
    parseFilenameFromContentDispositionHeader(headers["content-disposition"]) || `gp-report-${pmsFileId}.pdf`;
  const url = window.URL.createObjectURL(new Blob(parts, { type: contentType }));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Marks one outbound letter (from `gp_reports`) as the appointment's official
 * GP report. The backend copies the matched entry into `gp_report` so that the
 * Manage Tasks "Completed" auto-email knows which file to attach.
 */
export const markGPReportService = async ({ caId, pmsFileId }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.patch(
    `${process.env.REACT_APP_API_HOST}/admin/gp-report/mark`,
    { caId, pmsFileId },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

/**
 * Dispatches the download for one GP report entry. Routes to PMS when
 * `pmsFileId` is present (Wide Bay), otherwise falls back to the legacy GCS
 * endpoint. Returns true if a download was attempted, false if the entry has
 * neither identifier.
 */
export const triggerGPReportDownload = async (report) => {
  if (!report) return false;
  const { id, pmsFileId } = report;
  if (!id && !pmsFileId) return false;
  if (pmsFileId) {
    const res = await fetchGPReportFromPMS(pmsFileId);
    downloadPMSGPReport(res.data, pmsFileId, res.headers);
  } else {
    // For legacy (non-PMS) entries the GCS object key in `id` carries the
    // original filename including extension, e.g.
    // `2026-04-20_Patient_GP Report_<uuid>.pdf`. Use it directly so the
    // saved file keeps its extension — `name` on legacy records is often
    // just the patient name with no extension and would mis-route the
    // browser's MIME-sniffing fallback.
    const res = await fetchGPReport(id);
    downloadGPReport([res.data], id);
  }
  return true;
};
